import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { darken } from "polished";
import axios from "axios";
import { setQueryDrawerState } from "../../redux/slices/main";
import {
  setSearchQuery,
  setQueryLoadingState,
  setFeedbackDialogState,
  setConfigDialogState,
  setQueryResult,
  setPrevQueryResult,
  setClusters,
} from "../../redux/slices/search";
import { setBookmarkLoaded, setBookmarks } from "../../redux/slices/bookmark";
import { RootState } from "../../redux/store";
import { auth } from "../../firebase/init";
import {
  Grid,
  InputBase,
  AppBar as MuiAppBar,
  IconButton as MuiIconButton,
  Toolbar,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import useAppSelector from "../../hooks/useAppSelector";
import useAppDispatch from "../../hooks/useAppDispatch";
import { addQuery, getDocumentsByUUID } from "../../firebase/utils";
import { Bookmark } from "../../types/app";

const AppBar = styled(MuiAppBar)`
  background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};
`;

const IconButton = styled(MuiIconButton)`
  width: 22px;
  height: 22px;
  margin-left: 10px;

  &:hover {
    background-color: transparent;
  }
`;

const Search = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up("xs")} {
    display: block;
  }
`;

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Input = styled(InputBase)`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
    width: 100%;
  }
`;

type NavbarProps = {
  onDrawerToggle: React.MouseEventHandler<HTMLElement>;
};

const Navbar: React.FC<NavbarProps> = ({ onDrawerToggle }) => {
  const state = useAppSelector((state: RootState) => state.main);
  const searchState = useAppSelector((state: RootState) => state.search);
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pdfId } = useParams<{ pdfId: string }>();
  const [query, setQuery] = useState("");

  const handleSearch = async () => {
    try {
      // if (state.queryResult.length !== 0) {
      //   dispatch(setFeedbackDialogState());
      // }

      setQuery("");
      dispatch(setPrevQueryResult());
      navigate("/");

      dispatch(setQueryLoadingState(true));
      const encodedQuery = encodeURIComponent(query);
      const uid = auth.currentUser?.uid;
      const searchType = searchState.searchType;
      const limit = searchState.resultsToDisplay;

      const filesToSearchArray = Object.keys(searchState.filesToSearch);
      const filesToSearchStr = filesToSearchArray.join(";");
      const encodedFilenames = encodeURIComponent(filesToSearchStr);

      // const url = `https://docuai-c7a15.et.r.appspot.com/search?query=${encodedQuery}&uid=${uid}&logic=${logic}`;
      // const url = `https://docuai-staging.et.r.appspot.com/search?query=${encodedQuery}&uid=${uid}&logic=${logic}`;
      // const url = `http://127.0.0.1:5000/search?query=${encodedQuery}&uid=${uid}&logic=${logic}`;
      // const url = `http://127.0.0.1:5000/search?query=${encodedQuery}&uid=${uid}&search_type=${searchType}&limit=${limit}&filenames=${encodedFilenames}`;
      const url = `https://asia-southeast1-docusurf-87ced.cloudfunctions.net/search-main/search?query=${encodedQuery}&uid=${uid}&search_type=${searchType}&limit=${limit}&filenames=${encodedFilenames}`;
      // const url = `https://asia-southeast1-docuai-c7a15.cloudfunctions.net/search-staging/search?query=${encodedQuery}&uid=${uid}&search_type=${searchType}&limit=${limit}&filenames=${encodedFilenames}`;

      const response = await axios.get(url);
      const responseData = response.data;
      const clusterObj = responseData.cluster_results.clusters;

      if (responseData.results.length !== 0) {
        const clusters: number[] = [];
        const uniqueClusters = Object.keys(clusterObj).map((key: string) => {
          return clusterObj[key]["common_themes"].join(", ");
        });
        uniqueClusters.forEach((key: string, idx: number) => {
          const cluster = clusterObj[idx.toString()];
          cluster.sentences.forEach((sentence: any) => {
            clusters[sentence[1] - 1] = idx;
          });
        });
        const results = responseData.results.map(
          (result: any, index: number) => {
            result["cluster"] = clusters[index];
            return result;
          }
        );

        await addQuery(query, uniqueClusters, results);
      } else {
        dispatch(setQueryResult([]));
        dispatch(setClusters([]));
        dispatch(setSearchQuery(""));
      }
      dispatch(setQueryLoadingState(false));
    } catch (error) {
      navigate("/auth/500");
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    getDocumentsByUUID("bookmark").then((bookmarks: Bookmark[]) => {
      dispatch(setBookmarks(bookmarks.sort((a, b) => a.index - b.index)));
      dispatch(setBookmarkLoaded());
    });
  }, [
    bookmarkState.bookmarkDialogOpen,
    bookmarkState.bookmarkDeleteTracker,
    state.snackbarStatus,
  ]);

  return (
    <React.Fragment>
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Grid container alignItems="center" flexWrap="nowrap">
            <Grid item sx={{ display: { xs: "block", md: "none" } }}>
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={onDrawerToggle}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <Grid item sx={{ width: "100%", paddingRight: "20px" }}>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <Input
                  placeholder={"Search"}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </Search>
            </Grid>
            <IconButton onClick={() => dispatch(setConfigDialogState())}>
              <SettingsIcon />
            </IconButton>
            {pdfId !== undefined && (
              <Grid item display="flex">
                <IconButton
                  onClick={() =>
                    dispatch(setQueryDrawerState(!state.queryDrawerOpen))
                  }
                >
                  <MenuIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

export default withTheme(Navbar);
